import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Users, UserPlus, Loader2, Ban, Shield, Activity } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";

function RoleBadge({ role }) {
  const isOwner = role === "merchant";
  return (
    <span
      data-testid={`mteam-role-${role}`}
      className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${
        isOwner
          ? "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25"
          : "bg-[#114232]/8 text-[#114232] border-[#114232]/15"
      }`}
    >
      {isOwner ? "Owner" : "Staff"}
    </span>
  );
}

function InviteStaffModal({ open, onOpenChange, onInvited }) {
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/merchant/staff", form);
      toast.success("Staff member invited");
      setForm({ email: "", name: "", password: "" });
      onInvited();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-staff-dialog">
        <DialogHeader>
          <DialogTitle>Invite a staff member</DialogTitle>
          <DialogDescription>
            They'll be able to file claims and view your policies on your behalf,
            but cannot delete your merchant or invite further staff.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="invite-staff-form">
          <div>
            <Label htmlFor="is-email">Email</Label>
            <Input id="is-email" type="email" required data-testid="invite-staff-email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="is-name">Full name</Label>
            <Input id="is-name" required minLength={2} data-testid="invite-staff-name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="is-pwd">Temporary password (min 8)</Label>
            <Input id="is-pwd" type="password" required minLength={8} data-testid="invite-staff-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <div className="text-[11px] text-[#5c6360] mt-1">
              Share this with them privately — they can change it after first login.
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="invite-staff-submit">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MerchantTeam() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [tab, setTab] = useState("staff");

  const isOwner = user?.role === "merchant" || user?.role === "super_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/merchant/staff"),
        api.get("/merchant/activity?limit=50").catch(() => ({ data: { items: [] } })),
      ]);
      setStaff(r.data.items || []);
      setActivity(a.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function confirmRemove() {
    if (!removeTarget) return;
    try {
      await api.delete(`/merchant/staff/${removeTarget.id}`);
      toast.success(`Removed ${removeTarget.email}`);
      setRemoveTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-6 space-y-6" data-testid="merchant-team-page">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <Users className="w-6 h-6 text-[#c15337]" />
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#114232]">Your team</h1>
            <div className="text-xs text-[#5c6360]">
              Invite staff who can manage policies and file claims on your behalf.
            </div>
          </div>
        </div>
        {isOwner && (
          <Button
            data-testid="open-invite-staff"
            onClick={() => setInviteOpen(true)}
            className="bg-[#c15337] hover:bg-[#a3432a]"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> Invite staff
          </Button>
        )}
      </div>

      {!isOwner && (
        <div className="bg-[#d49a36]/10 border border-[#d49a36]/30 rounded-md p-3 text-sm text-[#8a5a16] flex items-center gap-2"
              data-testid="mteam-staff-banner">
          <Shield className="w-4 h-4 shrink-0" />
          You're signed in as staff — only the merchant owner can add or remove team members.
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-[#114232]/10 flex gap-1" data-testid="mteam-tabs">
        <button
          onClick={() => setTab("staff")}
          data-testid="mteam-tab-staff"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "staff"
              ? "border-[#c15337] text-[#114232]"
              : "border-transparent text-[#5c6360] hover:text-[#114232]"
          }`}
        >
          <Users className="w-3.5 h-3.5 inline mr-1.5" /> Team members
        </button>
        <button
          onClick={() => setTab("activity")}
          data-testid="mteam-tab-activity"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "activity"
              ? "border-[#c15337] text-[#114232]"
              : "border-transparent text-[#5c6360] hover:text-[#114232]"
          }`}
        >
          <Activity className="w-3.5 h-3.5 inline mr-1.5" /> Activity feed ({activity.length})
        </button>
      </div>

      {tab === "staff" && (
      <div className="bg-white border border-[#114232]/10 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="merchant-team-table">
          <thead className="text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-5">Name</th>
              <th className="text-left font-medium py-3 px-5">Email</th>
              <th className="text-left font-medium py-3 px-5">Role</th>
              <th className="text-left font-medium py-3 px-5">Status</th>
              <th className="text-right font-medium py-3 px-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-10 text-center">
                <Loader2 className="w-5 h-5 animate-spin inline text-[#114232]" />
              </td></tr>
            )}
            {!loading && staff.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-[#5c6360]">No team members yet.</td></tr>
            )}
            {!loading && staff.map((u) => (
              <tr key={u.id} className="border-t border-[#114232]/10"
                  data-testid={`mteam-row-${u.id}`}>
                <td className="py-3 px-5 font-medium text-[#114232]">{u.name}</td>
                <td className="py-3 px-5 font-mono text-xs">{u.email}</td>
                <td className="py-3 px-5"><RoleBadge role={u.role} /></td>
                <td className="py-3 px-5 text-xs capitalize">{u.status}</td>
                <td className="py-3 px-5 text-right">
                  {isOwner && u.role === "merchant_staff" && u.status !== "disabled" && (
                    <Button variant="ghost" size="sm"
                              onClick={() => setRemoveTarget(u)}
                              data-testid={`mteam-remove-${u.id}`}
                              className="text-[#c15337] hover:bg-[#c15337]/10">
                      <Ban className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {tab === "activity" && (
        <div className="bg-white border border-[#114232]/10 rounded-md" data-testid="mteam-activity-feed">
          {loading && (
            <div className="py-10 text-center">
              <Loader2 className="w-5 h-5 animate-spin inline text-[#114232]" />
            </div>
          )}
          {!loading && activity.length === 0 && (
            <div className="py-10 text-center text-[#5c6360]" data-testid="mteam-activity-empty">
              No activity recorded yet.
            </div>
          )}
          <ul className="divide-y divide-[#114232]/10">
            {activity.map((a) => (
              <li key={a.id} className="px-5 py-3" data-testid={`mteam-activity-row-${a.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#c15337]">
                      {a.action?.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs text-[#5c6360]">by {a.user_email || "system"}</span>
                  </div>
                  <span className="text-[10px] font-mono text-[#5c6360]">
                    {a.timestamp?.slice(0, 19).replace("T", " ")}
                  </span>
                </div>
                <div className="text-xs text-[#5c6360] mt-1 font-mono">
                  {a.entity_type}/{a.entity_id?.slice(0, 8)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <InviteStaffModal open={inviteOpen} onOpenChange={setInviteOpen} onInvited={load} />

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="mteam-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this staff member?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.email} will no longer be able to log in. You can
              re-invite them later with a new password if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="mteam-remove-confirm-btn"
              onClick={confirmRemove}
              className="bg-[#c15337] hover:bg-[#a3432a]"
            >Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
